"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Download, FileText, FileSpreadsheet, ShieldCheck, AlertTriangle } from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/app/_module/components/ui/card";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import { isDemoMode } from "@/app/_module/utils/demoMode";
import { Label } from "@/app/_module/components/ui/label";
import { Input } from "@/app/_module/components/ui/input";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/_module/components/ui/dialog";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { SimplifiedMFAModal } from "@/app/_module/components/SimplifiedMFAModal";
import { verifyMFAForAction, resendSMSForAction } from "@/app/_module/services/auth";
import { useExportProgress } from "./ExportProgressContext";

interface MasterExportColumn {
  key: string;
  label: string;
  category: 'basic' | 'contact' | 'location' | 'details' | 'disposition' | 'assignment' | 'policy' | 'audit';
}

interface DownlineAgent {
  id: string;
  name: string;
  email: string;
  agencyName: string;
  role: string;
  // Suspended / disabled agents stay listed so their book can still be exported.
  status?: 'active' | 'suspended' | 'disabled';
}

const CATEGORY_LABELS: Record<MasterExportColumn['category'], string> = {
  basic: 'Basic',
  contact: 'Contact',
  location: 'Location',
  details: 'Lead Details & Imported Fields',
  disposition: 'Disposition & Status',
  assignment: 'Assignment',
  policy: 'Policy Summary',
  audit: 'Audit / IDs',
};

/**
 * Master Downline Export
 *
 * Pulls leads across the agency owner's entire downline tree:
 *   - all sub-agencies (recursively)
 *   - personal/unassigned leads owned by anyone in those agencies
 *
 * Owner picks the columns to include, optional filters, format, then
 * confirms — the file streams back as a download. Audit row is always
 * persisted server-side. Rate-limited to 3 exports/hour.
 *
 * Shown only to top-of-tree agency owners. Sub-agency owners can run their
 * own subtree (backend enforces).
 */
export default function MasterExport({ sourceAgencyId }: { sourceAgencyId: string }) {
  const { toast } = useToast();
  const { user } = useUserContext();
  const { startJob } = useExportProgress();
  const [open, setOpen] = useState(false);
  const [columnsCatalog, setColumnsCatalog] = useState<MasterExportColumn[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [format, setFormat] = useState<'csv' | 'xlsx'>('csv');
  const [includeUnassignedPersonal, setIncludeUnassignedPersonal] = useState(true);
  // Agent picker — empty selection = the ENTIRE downline (default). Selecting
  // one or more agents narrows the export to leads owned by or assigned to
  // them, across every disposition stage.
  const [agents, setAgents] = useState<DownlineAgent[]>([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState<Set<string>>(new Set());
  const [agentSearch, setAgentSearch] = useState('');
  const [statusesInput, setStatusesInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  // MFA gate — mirrors the leads-page CSV export flow. Click Export →
  // stage the request body via ref (so the closure isn't stale on the
  // MFA callback) → open MFA modal → on verified, run the actual export.
  const [showMFAModal, setShowMFAModal] = useState(false);
  const pendingExportRef = useRef<any>(null);

  // Load column catalog when the modal opens (light request, no auth-sensitive data)
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get({
          url: getApiUrl(endpoint.agency.masterExportOptions),
          auth: true,
        });
        const data = (res as any)?.data?.data ?? (res as any)?.data;
        const cols: MasterExportColumn[] = data?.columns || [];
        setColumnsCatalog(cols);
        if (selectedKeys.size === 0) {
          // Sensible defaults — most common reporting set.
          // 'name' (Full Name) is FIRST and always on by default — agency
          // owners scanning a CSV want to see a person's whole name before
          // anything else. The backend writes columns in the order keys
          // appear in this array, so order here = column order in the CSV.
          setSelectedKeys(new Set([
            'name', 'firstName', 'lastName', 'phone', 'email',
            'leadStat', 'ownerUserName', 'assignedUserName', 'assignedUserStatus',
            // policyCarriers sits with the other policy columns: an owner
            // reviewing downline production wants to see WHO the business was
            // written with, not just how much. 'Carriers (All)' rather than the
            // most-recent one because a lead can hold policies with several.
            'policyCount', 'policyPremiumTotal', 'policyCoverageTotal', 'policyCarriers', 'policyNumbers', 'leadCreatedAt',
            // Everything the lead was imported with (old policy, carrier, beneficiary, ...).
            'age', 'customFields',
          ]));
        }
      } catch {
        toast({ title: 'Could not load export options', variant: 'destructive' });
      }
    })();
    // Downline agent roster for the picker (separate request — roster failure
    // shouldn't block the columns UI; picker just shows its empty state).
    (async () => {
      try {
        const res = await api.get({
          url: getApiUrl(`/agencies/master-export/agents?sourceAgencyId=${sourceAgencyId}`),
          auth: true,
        });
        const data = (res as any)?.data?.data ?? (res as any)?.data;
        setAgents(data?.agents || []);
      } catch {
        setAgents([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const grouped = useMemo(() => {
    const out: Record<string, MasterExportColumn[]> = {};
    for (const col of columnsCatalog) {
      (out[col.category] = out[col.category] || []).push(col);
    }
    return out;
  }, [columnsCatalog]);

  /**
   * Agents matching the search box. Hoisted out of the render body so the
   * "select all shown" control in the header can use the SAME list the rows
   * below render — otherwise the button and the list could disagree.
   *
   * Matches sub-agency name as well as name/email, so an owner can type a team
   * name and pull up just that team.
   */
  const visibleAgents = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q) ||
        a.agencyName.toLowerCase().includes(q) ||
        (a.status !== undefined && a.status !== 'active' && a.status.includes(q)),
    );
  }, [agents, agentSearch]);

  const toggleKey = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleCategory = (category: string) => {
    const cols = grouped[category] || [];
    const allSelected = cols.every((c) => selectedKeys.has(c.key));
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      for (const c of cols) {
        if (allSelected) next.delete(c.key);
        else next.add(c.key);
      }
      return next;
    });
  };

  // Spam guard — flips true the moment the user clicks Export and stays true
  // through MFA + job creation. Reset on MFA cancel, performExport finish, or
  // a short cooldown after successful submission. Held in a ref (not state)
  // so the check is synchronous and immune to React's batched re-renders —
  // rapid double-taps within the same event loop tick still get blocked.
  const inFlightRef = useRef(false);

  // Click "Export N columns" → validate → stage params → MFA modal.
  // Actual export runs after MFA verification (performExport below).
  const handleExportClick = () => {
    // DEMO SANDBOX: offer the trial instead of running the export.
    //
    // Master Export is the broadest data-egress path in the product — every agent in the
    // agency, every selected column, in one file. Gated at the click for the same reason
    // as the Leads export: letting someone pick columns and agents first, only to refuse
    // at the end, reads as a bug rather than a boundary.
    //
    // Placed above the re-entry guard so it can't be reached by a second click while a
    // previous one is still settling.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            'Master Export is disabled in the demo sandbox. Start a free trial to export your agency data.',
        }),
      );
      return;
    }

    // Hard re-entry guard. The button is already disabled via `loading`, but
    // belt-and-suspenders this prevents anything (keyboard repeat, devtools
    // synthetic click, accessibility tool) from firing the flow twice.
    if (inFlightRef.current || loading) return;
    if (selectedKeys.size === 0) {
      toast({ title: 'Select at least one column', variant: 'destructive' });
      return;
    }
    inFlightRef.current = true;
    setLoading(true);
    const statuses = statusesInput
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    // Sort selected keys by their position in columnsCatalog so the CSV
    // column order matches the canonical backend order regardless of which
    // order the user clicked the checkboxes. Keeps "Full Name" first, etc.
    const catalogOrder = new Map(columnsCatalog.map((c, i) => [c.key, i]));
    const orderedColumns = Array.from(selectedKeys).sort(
      (a, b) => (catalogOrder.get(a) ?? Number.MAX_SAFE_INTEGER) - (catalogOrder.get(b) ?? Number.MAX_SAFE_INTEGER),
    );
    pendingExportRef.current = {
      sourceAgencyId,
      format,
      columns: orderedColumns,
      // Agent picker: a proper subset narrows the export; none selected (or
      // literally everyone selected) = whole downline, no filter sent.
      ...(selectedAgentIds.size > 0 && selectedAgentIds.size < agents.length
        ? { agentUserIds: Array.from(selectedAgentIds) }
        : {}),
      ...(statuses.length ? { statuses } : {}),
      ...(dateFrom || dateTo
        ? { dateRange: { from: dateFrom || undefined, to: dateTo || undefined } }
        : {}),
      includeUnassignedPersonal,
    };
    setShowMFAModal(true);
  };

  // Wrapper for the MFA modal's verify callback (same signature the leads-page uses).
  const handleMFAVerify = async (code: string, method: 'totp' | 'sms'): Promise<boolean> => {
    try {
      return await verifyMFAForAction(code, method);
    } catch {
      return false;
    }
  };

  // Runs after MFA succeeds — kicks off a background job, then closes the
  // dialog and lets the ExportProgressWidget take over. Owner can close the
  // dialog, navigate away, come back — the widget keeps tracking.
  const performExport = async () => {
    const staged = pendingExportRef.current;
    if (!staged) {
      toast({ title: 'Export state lost — please try again', variant: 'destructive' });
      inFlightRef.current = false;
      setLoading(false);
      return;
    }
    // loading is already true from handleExportClick; keep it true through the
    // POST. Setting again is harmless and explicit.
    setLoading(true);
    let succeeded = false;
    try {
      const body = {
        ...staged,
        mfaVerifiedAt: new Date().toISOString(),
      };
      const res = await api.post<{ data: { jobId: string } }>({
        url: getApiUrl('/agencies/master-export/jobs'),
        body,
        auth: true,
      });
      // axiosConfig response interceptor already unwraps `response.data`,
      // so `res` IS the backend body { statusCode, message, data: { jobId } }.
      // The previous `res.data.data.jobId` was one level too deep — read
      // it back as undefined, the "Export failed to start" toast fired,
      // and the background job (which actually completed cleanly) was
      // orphaned because the widget never started polling.
      const jobId = (res as any)?.data?.jobId ?? (res as any)?.data?.data?.jobId;
      if (!jobId) {
        toast({ title: 'Export failed to start', variant: 'destructive' });
        return;
      }
      // Hand off to the floating widget — it polls and triggers the
      // download when complete.
      startJob(jobId);
      toast({
        title: 'Export queued',
        description: 'You can close this dialog — we’ll notify you when it’s ready.',
      });
      setOpen(false);
      succeeded = true;
    } catch (err: any) {
      // 409 means user already has an active export. The orchestrator's
      // message is user-friendly, surface it as-is.
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Could not start export';
      toast({ title: message, variant: 'destructive' });
    } finally {
      pendingExportRef.current = null;
      if (succeeded) {
        // Cooldown after success — block re-fires for 2s so the widget has
        // time to appear and the user doesn't double-tap before they see
        // their export in flight.
        setTimeout(() => {
          inFlightRef.current = false;
          setLoading(false);
        }, 2000);
      } else {
        // Error path — release immediately so the user can correct + retry.
        inFlightRef.current = false;
        setLoading(false);
      }
    }
  };

  return (
    <>
      <Card className="border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Master Downline Export
          </CardTitle>
          <CardDescription>
            Export leads from your entire downline (sub-agencies + their agents&apos; personal leads).
            Audited. Watermarked. Limited to 3 exports/hour.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mx-card-row flex items-center justify-between gap-4">
            <div className="flex items-start gap-2 text-sm text-amber-700">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                Every export is tracked: who, when, IP, columns, row count. Files are
                watermarked so leaks can be traced. Confidential.
              </p>
            </div>
            <Button onClick={() => setOpen(true)}>
              <Download className="h-4 w-4 mr-2" />
              Configure Export
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <ResponsiveDialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Master Downline Export</DialogTitle>
            <DialogDescription>
              Choose what to include. Backend gathers leads from {''}
              <strong>every sub-agency and every agent&apos;s personal pool</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
            {/* Format */}
            <div>
              <Label className="text-sm font-semibold">Format</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  size="sm"
                  variant={format === 'csv' ? 'default' : 'outline'}
                  onClick={() => setFormat('csv')}
                >
                  <FileText className="h-4 w-4 mr-2" /> CSV
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={format === 'xlsx' ? 'default' : 'outline'}
                  onClick={() => setFormat('xlsx')}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
                </Button>
              </div>
            </div>

            {/* Scope */}
            <div>
              <Label className="text-sm font-semibold">Scope</Label>
              <div className="flex items-center gap-2 mt-2">
                <Checkbox
                  id="include-personal"
                  checked={includeUnassignedPersonal}
                  onCheckedChange={(v) => setIncludeUnassignedPersonal(!!v)}
                />
                <Label htmlFor="include-personal" className="text-sm font-normal cursor-pointer">
                  Include agents&apos; personal / unassigned leads
                </Label>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Off = only leads assigned to a user or agency. On = adds personal leads each agent owns.
              </p>
            </div>

            {/* Agents — pick who to export. Nothing selected = entire downline. */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">
                  Agents{' '}
                  {selectedAgentIds.size > 0 ? (
                    <span className="text-primary font-normal">({selectedAgentIds.size} selected)</span>
                  ) : (
                    <span className="text-muted-foreground font-normal">(all — entire downline)</span>
                  )}
                </Label>
                <div className="flex items-center gap-1">
                  {/* Select-all applies to the CURRENTLY SHOWN agents, so searching
                      a sub-agency then clicking this selects that whole team in one
                      click instead of ticking each row. Additive — it never clears
                      picks made under a previous search. */}
                  {visibleAgents.length > 1 && (
                    <Button
                      type="button" size="sm" variant="ghost"
                      onClick={() =>
                        setSelectedAgentIds((prev) => {
                          const next = new Set(prev);
                          const allShown = visibleAgents.every((a) => next.has(a.id));
                          // Toggle: if everything shown is already picked, unpick just those.
                          for (const a of visibleAgents) {
                            if (allShown) next.delete(a.id); else next.add(a.id);
                          }
                          return next;
                        })
                      }
                    >
                      {visibleAgents.every((a) => selectedAgentIds.has(a.id))
                        ? `Deselect ${visibleAgents.length}`
                        : `Select ${visibleAgents.length}${agentSearch.trim() ? ' shown' : ''}`}
                    </Button>
                  )}
                  {selectedAgentIds.size > 0 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => setSelectedAgentIds(new Set())}>
                      Clear — export all
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Select one or more agents to export only their leads (owned or assigned, every disposition stage).
                Suspended and disabled agents are listed too. Leave empty to export the entire downline.
              </p>
              {/* ALWAYS rendered when there is anything to search (2026-07-28).
                  Previously gated behind `agents.length > 6`, which meant the
                  control vanished on small agencies — so nobody could tell the
                  feature existed, and the list already scrolls inside its
                  max-h-48 box from ~5 rows. The placeholder doubles as a roster
                  count, which is useful at any size. */}
              {agents.length > 1 && (
                <Input
                  placeholder={`Search ${agents.length} agents by name, email, or sub-agency…`}
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                  className="h-8 text-sm mb-2"
                />
              )}
              {agents.length === 0 ? (
                <p className="text-xs text-gray-400 border rounded p-3">No downline agents found.</p>
              ) : (() => {
                // Uses the hoisted visibleAgents so this list and the
                // "Select N shown" button above can never diverge.
                const visible = visibleAgents;
                if (visible.length === 0) {
                  return <p className="text-xs text-gray-400 border rounded p-3">No agents match “{agentSearch}”</p>;
                }
                return (
                  <div className="border rounded max-h-48 overflow-y-auto divide-y">
                    {visible.map((a) => {
                      const checked = selectedAgentIds.has(a.id);
                      return (
                        <label key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => {
                              setSelectedAgentIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(a.id)) next.delete(a.id); else next.add(a.id);
                                return next;
                              });
                            }}
                          />
                          <span className="truncate">{a.name}</span>
                          {(a.status === 'suspended' || a.status === 'disabled') && (
                            <span
                              className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
                                a.status === 'disabled'
                                  ? 'bg-red-50 text-red-700 ring-red-600/20'
                                  : 'bg-amber-50 text-amber-700 ring-amber-600/20'
                              }`}
                            >
                              {a.status === 'disabled' ? 'Disabled' : 'Suspended'}
                            </span>
                          )}
                          <span className="ml-auto text-xs text-muted-foreground truncate max-w-[40%]">{a.agencyName}</span>
                        </label>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Filters */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Filters (optional)</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Statuses (comma-separated)</Label>
                  <Input
                    placeholder='New lead, Appt, Sold'
                    value={statusesInput}
                    onChange={(e) => setStatusesInput(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Lead Created From</Label>
                    <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">To</Label>
                    <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Columns */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">
                  Columns ({selectedKeys.size} selected)
                </Label>
              </div>
              <div className="space-y-4">
                {Object.entries(grouped).map(([category, cols]) => {
                  const allSelected = cols.every((c) => selectedKeys.has(c.key));
                  return (
                    <div key={category} className="border rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium">
                          {CATEGORY_LABELS[category as MasterExportColumn['category']] || category}
                        </h4>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleCategory(category)}
                        >
                          {allSelected ? 'Clear group' : 'Select all'}
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {cols.map((col) => (
                          <label key={col.key} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                              checked={selectedKeys.has(col.key)}
                              onCheckedChange={() => toggleKey(col.key)}
                            />
                            <span>{col.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                This file will be watermarked with a unique tracking ID tied to your account.
                Sharing externally is prohibited and traceable.
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleExportClick} disabled={loading || selectedKeys.size === 0}>
              {loading ? 'Generating…' : `Export ${selectedKeys.size} columns`}
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      {/* MFA Verification — same component + flow as the leads-page CSV export.
          User clicks "Export N columns" → MFA modal → on verified, performExport()
          runs the actual download. Without this gate the master export would be
          unauthenticated to the second factor, which is exactly what the existing
          leads export requires for sensitive data egress. */}
      <SimplifiedMFAModal
        isOpen={showMFAModal}
        onClose={() => {
          setShowMFAModal(false);
          pendingExportRef.current = null;
          // User cancelled MFA — release the spam guard so they can retry.
          inFlightRef.current = false;
          setLoading(false);
        }}
        onVerified={() => {
          setShowMFAModal(false);
          performExport();
        }}
        onVerify={handleMFAVerify}
        onSendSMS={resendSMSForAction}
        title="Verify Identity to Export Leads"
        description="For security, please verify your identity with 2FA before exporting downline lead data."
      />
    </>
  );
}
